/* eslint-disable no-console */
import React from 'react';
import PropTypes from 'prop-types';
import { reportError } from 'helpers/error-logging';
import { isRedirectingToLogin as readRedirectFlag } from 'helpers/session-auth-redirect';
import { dispatchNetworkErrorSnackbar, isChunkLoadError, isResizeObserverLoopError } from 'helpers/transport-error';
import InternalErrorComponent from '../InternalError';

class ComponentErrorBoundary extends React.Component {
  static propTypes = {
    children: PropTypes.node,
  };

  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkLoadError: false };
  }

  static getDerivedStateFromError(error) {
    if (isResizeObserverLoopError(error)) return { hasError: false, isChunkLoadError: false };
    if (isChunkLoadError(error)) return { hasError: true, isChunkLoadError: true };
    return { hasError: true, isChunkLoadError: false };
  }

  componentDidUpdate(prevProps) {
    const { children } = this.props;
    const { hasError } = this.state;
    if (hasError && prevProps.children !== children) {
      // Route/content changed — give the new tree a clean slate so a chunk-load failure
      // on one navigation doesn't blank out subsequent navigations.
      this.setState({ hasError: false, isChunkLoadError: false });
    }
  }

  componentDidCatch(error) {
    if (isResizeObserverLoopError(error)) return;

    if (isChunkLoadError(error)) {
      dispatchNetworkErrorSnackbar({ force: true });
      return;
    }

    if (process.env.NODE_ENV === 'test') {
      console.log(error);
    }

    // Suppress error reporting during the auth-redirect window: render crashes here are
    // expected fallout from the 401/403 that triggered the redirect, not real bugs.
    if (readRedirectFlag()) return;

    const { children } = this.props;
    const originalName = children?.displayName || children?.name || 'unknown component';
    reportError(`<ComponentErrorBoundary /> caught error in: ${originalName}`, error);
  }

  render() {
    const { children } = this.props;
    const { hasError, isChunkLoadError: chunkLoadError } = this.state;

    if (!hasError) return children ?? null;

    // Avoid flashing the maintenance overlay while a logout redirect is in flight.
    if (readRedirectFlag()) return null;

    // Chunk load failures (typically offline) surface as a recoverable snackbar from
    // componentDidCatch — render an empty page so the user can pick another sidebar link.
    if (chunkLoadError) return null;

    return <InternalErrorComponent />;
  }
}

export { ComponentErrorBoundary };
export default ComponentErrorBoundary;
